import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { selectIsRTL } from '../../redux/selectors/displaySettingsSelectors';

// Custom hooks
import { useChartConfig } from './hooks/useChartConfig';

// Shared components
import { ChartContainer } from './shared/ChartContainer';
import { ChartContent } from './shared/ChartContent';
import { ChartFooter } from './shared/ChartFooter';
import { IWeeklyLogAnalyticsResponse } from '../../common/interfaces/WeeklyLogAnalyticsResponse.interface';

interface WeeklyLogAgeTabProps {
  analyticsData: IWeeklyLogAnalyticsResponse;
}

export const WeeklyLogAgeTab: React.FC<WeeklyLogAgeTabProps> = ({
  analyticsData,
}) => {
  const { t } = useTranslation();
  const isRTL = useAppSelector(selectIsRTL);
  const config = useChartConfig();

  if (
    !analyticsData.totalLogs.ageDistribution ||
    analyticsData.totalLogs.ageDistribution.length === 0
  ) {
    return (
      <ChartContainer>
        <div className="p-4 text-center">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('demographics.ageGroupsDistribution')}
          </h2>
          <p className="text-sm text-gray-700">{t('analytics.noData')}</p>
        </div>
      </ChartContainer>
    );
  }

  const ageData = analyticsData.totalLogs.ageDistribution.map((item) => ({
    ageGroup: item.value,
    count: item.count,
  }));

  return (
    <ChartContainer>
      {/* Header */}
      <div className="p-4 text-center">
        <h2 className="text-lg font-semibold text-gray-900">
          {t('demographics.ageGroupsDistribution')}
        </h2>
        <p className="text-sm text-gray-700">
          {t('demographics.ageGroupsDistributionDescription')}
        </p>
      </div>

      {/* Chart */}
      <ChartContent>
        <ResponsiveContainer
          width="100%"
          height={360}
          style={config.styles.noOutline}
        >
          <BarChart
            data={ageData}
            margin={{
              top: 25,
              right: isRTL ? 20 : 20,
              left: isRTL ? -20 : 0,
            }}
            barSize={20}
            style={config.styles.noOutline}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={config.colors.grid} />
            <XAxis
              dataKey="ageGroup"
              scale="point"
              padding={{ left: 75, right: 75 }}
              {...config.axis}
            />
            <YAxis {...config.axis} />
            <Tooltip
              {...config.tooltip}
              formatter={(value, name) => {
                return [value, name];
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Legend {...config.legend} />
            <Bar
              dataKey="count"
              name={t('dashboard.dataLabels.participants')}
              fill={config.colors.primary}
              background={{ fill: config.colors.barBackground }}
              style={config.styles.noOutline}
              radius={[5, 5, 0, 0]}
              activeBar={{ fill: config.colors.primary, opacity: 0.6 }}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContent>

      {/* Footer */}
      <ChartFooter>
        {t('demographics.ageGroupsDistributionShowing')}
      </ChartFooter>
    </ChartContainer>
  );
};
